import React, { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getApiBase } from '../lib/utils'
import { api, systemPromptsApi, SystemPromptListItem, memoryApi, MemoryScope } from '../lib/api'
import AgentMCPDrawer from '../components/AgentMCPDrawer'
import WhatsAppTab from '../components/WhatsAppTab'
import AgentSandboxChat, { type AgentSummary } from '../components/AgentSandboxChat'
import AgentEditorModal from '../components/AgentEditorModal'
import type { LlmModelOption } from '../types/llmModel'
import AgentModelPickerModal from '../components/AgentModelPickerModal'
import DuplicateAgentModal from '../components/DuplicateAgentModal'
import { HelpTooltip } from '../components/Tooltip'
import { useAgentTeams } from '../hooks/useAgentTeams'
import TeamsSidebar from '../components/agents/TeamsSidebar'
import AgentCard from '../components/agents/AgentCard'
import AgentOnboardingGuide from '../components/onboarding/AgentOnboardingGuide'
import AgentTypeSelector from '../components/AgentTypeSelector'
import TemplateLibrary from '../components/TemplateLibrary'
import { AgentType, PromptTemplate } from '../data/promptTemplates'
import { useAppLanguage } from '../i18n'

function getFormatters(locale: string) {
  return {
    numberFormatter: new Intl.NumberFormat(locale),
    dateFormatter: new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' })
  }
}

export type Agent = {
  id: number
  owner_user_id: number
  name: string
  slug: string
  icon_url?: string | null
  icon_preview_url?: string | null
  subtitle?: string | null
  instructions?: string | null
  message_limit?: number | null
  model_id?: string | null
  top_k?: number | null
  rerank_top_n: number
  disable_own_knowledge: boolean
  is_active: boolean
  subject_ids: number[]
  team_id?: number | null
  system_prompt_id?: number | null  // Admin-only: override system prompt
  memory_enabled?: boolean
  memory_end_user_enabled?: boolean
  auto_extract_facts?: boolean
  auto_extract_min_confidence?: number
  usage: { message_count: number; limit?: number | null }
  owner_usage: { message_count: number; limit?: number | null }
  created_at: string
  updated_at: string
}

type AgentQuota = {
  max_active: number | null
  current_active: number
  available: number | null
  total_agents: number
  global_active: number
  global_total: number
}

type Subject = { id: number; title: string; source: string }

type FormState = {
  name: string
  subtitle: string
  instructions: string
  messageLimit: string
  modelId: string
  topK: string
  rerankTopN: string
  disableOwnKnowledge: boolean
  subjectIds: number[]
  systemPromptId: number | null  // Admin-only
  teamId: number | null
  memoryEnabled: boolean
  memoryEndUserEnabled: boolean
  autoExtractFacts: boolean
  autoExtractMinConfidence: number
}

const emptyForm: FormState = {
  name: '',
  subtitle: '',
  instructions: '',
  messageLimit: '',
  modelId: '',
  topK: '10',
  rerankTopN: '5',
  disableOwnKnowledge: false,
  subjectIds: [],
  systemPromptId: null,
  teamId: null,
  memoryEnabled: true,
  memoryEndUserEnabled: false,
  autoExtractFacts: false,
  autoExtractMinConfidence: 0.75,
}

export default function AgentsPage() {
  const navigate = useNavigate()
  const { t, language } = useAppLanguage()
  const { numberFormatter, dateFormatter } = useMemo(() => {
    const locale = language === 'en' ? 'en-US' : language === 'es' ? 'es-ES' : 'fr-FR'
    return getFormatters(locale)
  }, [language])
  
  const interpolate = (template: string, vars: Record<string, string | number>) => {
    return Object.entries(vars).reduce(
      (acc, [key, val]) => acc.replace(`{${key}}`, String(val)),
      template
    )
  }

  const [agents, setAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [quota, setQuota] = useState<AgentQuota | null>(null)
  const [quotaLoading, setQuotaLoading] = useState(false)

  const [subjects, setSubjects] = useState<Subject[]>([])
  const [subjectsLoading, setSubjectsLoading] = useState(false)

  const [llmModels, setLlmModels] = useState<LlmModelOption[]>([])
  const [llmModelsLoading, setLlmModelsLoading] = useState(false)

  // Current user (for admin check)
  const [currentUser, setCurrentUser] = useState<{ is_superuser?: boolean } | null>(null)
  const isAdmin = currentUser?.is_superuser === true

  // System prompts (admin-only)
  const [systemPrompts, setSystemPrompts] = useState<SystemPromptListItem[]>([])

  // Agent teams
  const { 
    teams, 
    isLoading: teamsLoading,
    create: createTeam,
    rename: renameTeam,
    remove: removeTeam,
    refetch: refetchTeams,
  } = useAgentTeams()
  const [selectedTeamId, setSelectedTeamId] = useState<number | 'orphan' | null>(null)
  const [teamSidebarOpen, setTeamSidebarOpen] = useState(false)

  const [createForm, setCreateForm] = useState<FormState>(emptyForm)
  const [createIcon, setCreateIcon] = useState<File | null>(null)
  const [creating, setCreating] = useState(false)
  const [createFeedback, setCreateFeedback] = useState<string | null>(null)
  const [createError, setCreateError] = useState<string | null>(null)
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false)
  const [teamModalOpen, setTeamModalOpen] = useState(false)
  const [statsDrawerOpen, setStatsDrawerOpen] = useState(false)
  const [mcpDrawerOpen, setMcpDrawerOpen] = useState(false)
  const [mcpDrawerAgentId, setMcpDrawerAgentId] = useState<number | null>(null)
  const [mcpDrawerAgentName, setMcpDrawerAgentName] = useState('')

  const [editingAgent, setEditingAgent] = useState<Agent | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editForm, setEditForm] = useState<FormState>(emptyForm)
  const [editError, setEditError] = useState<string | null>(null)
  const [savingEdit, setSavingEdit] = useState(false)
  const [sandboxConversationId, setSandboxConversationId] = useState<number | null>(null)

  const [deleteTarget, setDeleteTarget] = useState<Agent | null>(null)
  const [deletePending, setDeletePending] = useState(false)
  const [duplicateAgentId, setDuplicateAgentId] = useState<number | null>(null)
  const [duplicateAgentName, setDuplicateAgentName] = useState('')
  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [iconUploadingId, setIconUploadingId] = useState<number | null>(null)
  const iconUrlsRef = useRef<string[]>([])

  const loadAgents = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Récupérer le workspace_id actuel depuis localStorage
      const currentWorkspaceId = localStorage.getItem('current_workspace_id')
      const params = currentWorkspaceId ? { workspace_id: currentWorkspaceId } : {}
      
      const { data } = await api.get('/agents', { params })
      const items: Agent[] = Array.isArray(data?.items) ? data.items : []

      const nextBlobUrls: string[] = []
      const enriched: Agent[] = await Promise.all(
        items.map(async (agent) => {
          if (agent.icon_url) {
            try {
              const response = await api.get(agent.icon_url, { responseType: 'blob' })
              const blob: Blob = response.data as Blob
              const objectUrl = URL.createObjectURL(blob)
              nextBlobUrls.push(objectUrl)
              return { ...agent, icon_preview_url: objectUrl }
            } catch {
              return { ...agent, icon_preview_url: null }
            }
          }
          return { ...agent, icon_preview_url: null }
        })
      )

      iconUrlsRef.current.forEach((url) => {
        try { URL.revokeObjectURL(url) } catch {}
      })
      iconUrlsRef.current = nextBlobUrls

      setAgents(enriched)
      try {
        window.dispatchEvent(new CustomEvent('agents-updated', { detail: { ids: enriched.map(agent => agent.id) } }))
      } catch (eventError) {
        console.warn('[AgentsPage] dispatch agents-updated failed', eventError)
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || t.agents.errors.loadAgents
      setError(detail)
      setAgents([])
    } finally {
      setLoading(false)
    }
  }, [])

  const loadQuota = useCallback(async () => {
    setQuotaLoading(true)
    try {
      const currentWorkspaceId = localStorage.getItem('current_workspace_id')
      const params = currentWorkspaceId ? { workspace_id: currentWorkspaceId } : {}
      const { data } = await api.get('/agents/quota', { params })
      setQuota(data as AgentQuota)
    } catch (err: any) {
      console.warn('[AgentsPage] Failed to load quota:', err)
      setQuota(null)
    } finally {
      setQuotaLoading(false)
    }
  }, [])

  const loadSubjects = useCallback(async () => {
    setSubjectsLoading(true)
    try {
      const { data } = await api.get('/subjects')
      if (Array.isArray(data)) setSubjects(data as Subject[])
      else if (Array.isArray(data?.items)) setSubjects(data.items as Subject[])
      else setSubjects([])
    } catch {
      setSubjects([])
    } finally {
      setSubjectsLoading(false)
    }
  }, [])

  const loadLlmModels = useCallback(async () => {
    setLlmModelsLoading(true)
    try {
      const currentWorkspaceId = localStorage.getItem('current_workspace_id')
      const params = currentWorkspaceId ? { workspace_id: currentWorkspaceId } : {}
      const { data } = await api.get('/models', { params })
      if (data?.models && Array.isArray(data.models)) {
        setLlmModels(
          data.models
            .map((m: { name?: string; provider?: string; description?: string }) => ({
              name: m.name || '',
              provider: (m.provider || 'ollama').toLowerCase(),
              description: m.description,
            }))
            .filter((m: LlmModelOption) => m.name)
        )
      } else {
        setLlmModels([])
      }
    } catch {
      setLlmModels([])
    } finally {
      setLlmModelsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadAgents()
    loadQuota()
    loadSubjects()
    loadLlmModels()
    // Load current user
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null')
      setCurrentUser(user)
    } catch {}
  }, [loadAgents, loadQuota, loadSubjects, loadLlmModels])

  // Load system prompts if admin
  useEffect(() => {
    if (!isAdmin) return
    systemPromptsApi.list().then(result => {
      setSystemPrompts(result.items)
    }).catch(err => {
      console.warn('[AgentsPage] Failed to load system prompts:', err)
    })
  }, [isAdmin])

  useEffect(() => {
    return () => {
      iconUrlsRef.current.forEach((url) => {
        try { URL.revokeObjectURL(url) } catch {}
      })
      iconUrlsRef.current = []
    }
  }, [])

  const ownerUsage = agents[0]?.owner_usage ?? null
  const subjectMap = useMemo(() => {
    const map = new Map<number, Subject>()
    subjects.forEach(s => map.set(s.id, s))
    return map
  }, [subjects])

  // Agent counts by team
  const agentCountByTeam = useMemo(() => {
    const counts: Record<number, number> = {}
    for (const agent of agents) {
      if (agent.team_id) {
        counts[agent.team_id] = (counts[agent.team_id] || 0) + 1
      }
    }
    return counts
  }, [agents])

  // Orphan agents count (no team)
  const orphanAgentCount = useMemo(() => {
    return agents.filter(a => !a.team_id).length
  }, [agents])

  // Filtered agents based on selected team
  const filteredAgents = useMemo(() => {
    if (selectedTeamId === null) return agents
    if (selectedTeamId === 'orphan') return agents.filter(a => !a.team_id)
    return agents.filter(a => a.team_id === selectedTeamId)
  }, [agents, selectedTeamId])

  useEffect(() => {
    if (!editingAgent) return
    const latest = agents.find(a => a.id === editingAgent.id)
    if (latest) {
      setEditingAgent(latest)
    }
  }, [agents, editingAgent])

  // Listen for onboarding event to close sandbox modal
  useEffect(() => {
    const handleCloseSandbox = () => {
      setIsEditModalOpen(false)
      setEditingAgent(null)
      setEditForm(emptyForm)
      setEditError(null)
      setSandboxConversationId(null)
    }
    window.addEventListener('onboarding:close-sandbox', handleCloseSandbox)
    return () => window.removeEventListener('onboarding:close-sandbox', handleCloseSandbox)
  }, [])

  const overviewStats = useMemo(() => {
    const ownerCount = ownerUsage?.message_count ?? 0
    const ownerLimit = ownerUsage?.limit ?? null
    const remaining = ownerLimit != null ? Math.max(ownerLimit - ownerCount, 0) : null
    return [
      {
        label: t.agents.stats.activeAgents,
        value: numberFormatter.format(agents.length),
        description: agents.length > 0 ? t.agents.stats.activeAgentsDesc : t.agents.stats.activeAgentsEmpty,
      },
      {
        label: t.agents.stats.messagesSent,
        value: numberFormatter.format(ownerCount),
        description: ownerLimit != null ? interpolate(t.agents.stats.messagesRemaining, { n: numberFormatter.format(remaining ?? 0) }) : t.agents.stats.noLimit,
      },
      {
        label: t.agents.stats.globalQuota,
        value: ownerLimit != null ? numberFormatter.format(ownerLimit) : '∞',
        description: ownerLimit != null ? t.agents.stats.limitLabel : t.agents.stats.unlimitedPlan,
      },
      {
        label: t.agents.stats.lastUpdate,
        value: agents.length ? dateFormatter.format(new Date(agents[0].updated_at)) : '—',
        description: agents.length ? t.agents.stats.recentAgent : t.agents.stats.noAgentYet,
      },
    ]
  }, [agents, ownerUsage, t, numberFormatter, dateFormatter, interpolate])

  const updateCreateSubjectIds = (id: number) => {
    setCreateForm(prev => {
      const next = prev.subjectIds.includes(id)
        ? prev.subjectIds.filter(sid => sid !== id)
        : [...prev.subjectIds, id]
      return { ...prev, subjectIds: next }
    })
  }

  const updateEditSubjectIds = (id: number) => {
    setEditForm(prev => {
      const next = prev.subjectIds.includes(id)
        ? prev.subjectIds.filter(sid => sid !== id)
        : [...prev.subjectIds, id]
      return { ...prev, subjectIds: next }
    })
  }

  const resetCreateForm = () => {
    setCreateForm(emptyForm)
    setCreateIcon(null)
  }

  const openCreateDrawer = () => {
    resetCreateForm()
    setCreateError(null)
    setCreateFeedback(null)
    setCreateDrawerOpen(true)
  }

  const closeCreateDrawer = () => {
    setCreateDrawerOpen(false)
    resetCreateForm()
    setCreateError(null)
  }

  const openStatsDrawer = () => {
    setStatsDrawerOpen(true)
  }

  const closeStatsDrawer = () => {
    setStatsDrawerOpen(false)
  }

  const openMcpDrawer = (agentId: number, agentName: string) => {
    setMcpDrawerAgentId(agentId)
    setMcpDrawerAgentName(agentName)
    setMcpDrawerOpen(true)
  }

  const closeMcpDrawer = () => {
    setMcpDrawerOpen(false)
    setMcpDrawerAgentId(null)
    setMcpDrawerAgentName('')
  }

  const onCreateSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!createForm.name.trim()) {
      setCreateError('Le nom est requis')
      return
    }
    setCreating(true)
    setCreateError(null)
    setCreateFeedback(null)
    try {
      const payload: any = {
        name: createForm.name.trim(),
        subtitle: createForm.subtitle.trim() || undefined,
        instructions: createForm.instructions.trim() || undefined,
        message_limit: createForm.messageLimit ? Number(createForm.messageLimit) : undefined,
        model_id: createForm.modelId.trim() || undefined,
        top_k: createForm.topK ? Number(createForm.topK) : undefined,
        rerank_top_n: createForm.rerankTopN ? Number(createForm.rerankTopN) : 5,
        disable_own_knowledge: createForm.disableOwnKnowledge,
        subject_ids: createForm.subjectIds,
      }
      const { data } = await api.post('/agents', payload)
      if (createIcon) {
        const form = new FormData()
        form.append('file', createIcon)
        await api.post(`/agents/${data.id}/icon`, form)
      }
      await loadAgents()
      setCreateDrawerOpen(false)
      resetCreateForm()
      setCreateFeedback(`Agent « ${data.name} » créé`)
      try {
        localStorage.setItem('chat_selected_agent', String(data.id))
        window.dispatchEvent(new CustomEvent('chat-agent-selected', { detail: data.id }))
      } catch {}
    } catch (err: any) {
      const rawDetail = err?.response?.data?.detail
      // Handle object error responses (quota errors have {error, message, ...})
      const detail = typeof rawDetail === 'object' && rawDetail !== null
        ? rawDetail.message || rawDetail.error || JSON.stringify(rawDetail)
        : rawDetail || err?.message || t.agents.errors.createAgent
      setCreateError(detail)
    } finally {
      setCreating(false)
    }
  }

  const startEdit = (agent: Agent) => {
    setEditingAgent(agent)
    setIsEditModalOpen(true)
    setEditForm({
      name: agent.name,
      subtitle: agent.subtitle || '',
      instructions: agent.instructions || '',
      messageLimit: agent.message_limit != null ? String(agent.message_limit) : '',
      modelId: agent.model_id || '',
      topK: agent.top_k != null ? String(agent.top_k) : '10',
      rerankTopN: agent.rerank_top_n != null ? String(agent.rerank_top_n) : '5',
      disableOwnKnowledge: agent.disable_own_knowledge || false,
      subjectIds: [...agent.subject_ids],
      systemPromptId: agent.system_prompt_id ?? null,
      teamId: agent.team_id ?? null,
      memoryEnabled: agent.memory_enabled ?? true,
      memoryEndUserEnabled: agent.memory_end_user_enabled ?? false,
      autoExtractFacts: agent.auto_extract_facts ?? false,
      autoExtractMinConfidence: agent.auto_extract_min_confidence ?? 0.75,
    })
    setEditError(null)
  }

  const cancelEdit = () => {
    setIsEditModalOpen(false)
    setEditingAgent(null)
    setEditForm(emptyForm)
    setEditError(null)
    setSandboxConversationId(null)
  }

  const saveEdit = async () => {
    if (!editingAgent) return
    setSavingEdit(true)
    setEditError(null)
    try {
      // Check if model changed (to allow backend auto-configuration of top_k)
      const modelChanged = editingAgent.model_id !== (editForm.modelId.trim() || null)
      
      const payload: any = {
        name: editForm.name.trim() || undefined,
        subtitle: editForm.subtitle.trim() || null,
        instructions: editForm.instructions.trim() || null,
        message_limit: editForm.messageLimit === '' ? null : Number(editForm.messageLimit),
        model_id: editForm.modelId.trim() || null,
        // top_k: don't send if model changed (let backend auto-configure)
        // Only send if admin AND model didn't change (preserving manual override)
        ...(!modelChanged && isAdmin && { top_k: editForm.topK === '' ? null : Number(editForm.topK) }),
        rerank_top_n: editForm.rerankTopN ? Number(editForm.rerankTopN) : 5,
        disable_own_knowledge: editForm.disableOwnKnowledge,
        subject_ids: editForm.subjectIds,
        // Admin-only: system prompt override
        ...(isAdmin && { system_prompt_id: editForm.systemPromptId }),
        // Team assignment: 0 means remove from team, null means no change
        team_id: editForm.teamId === null ? (editingAgent.team_id ? 0 : undefined) : editForm.teamId,
      }
      const response = await api.patch(`/agents/${editingAgent.id}`, payload)
      await loadAgents()
      // Update editingAgent with new data (don't close modal)
      setEditingAgent(response.data)
      // Update form with new values from server (e.g., auto-configured top_k)
      setEditForm(prev => ({
        ...prev,
        topK: response.data.top_k != null ? String(response.data.top_k) : '10',
        rerankTopN: response.data.rerank_top_n != null ? String(response.data.rerank_top_n) : '5',
        modelId: response.data.model_id || '',
        teamId: response.data.team_id ?? null,
      }))
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || t.agents.errors.updateAgent
      setEditError(detail)
    } finally {
      setSavingEdit(false)
    }
  }

  const requestDeleteAgent = (agent: Agent) => {
    setDeleteTarget(agent)
    setDeleteError(null)
  }

  const cancelDeleteAgent = () => {
    setDeleteTarget(null)
    setDeleteError(null)
    setDeletePending(false)
  }

  const confirmDeleteAgent = async () => {
    if (!deleteTarget) return
    setDeletePending(true)
    try {
      // Récupérer le workspace_id actuel
      const currentWorkspaceId = localStorage.getItem('current_workspace_id')
      const params = currentWorkspaceId ? { workspace_id: currentWorkspaceId } : {}
      
      await api.delete(`/agents/${deleteTarget.id}`, { params })
      if (editingAgent?.id === deleteTarget.id) cancelEdit()
      // Recharger les agents ET le quota
      await Promise.all([loadAgents(), loadQuota()])
      cancelDeleteAgent()
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || t.agents.errors.deleteAgent
      setDeleteError(detail)
      setDeletePending(false)
    }
  }

  const openDuplicateModal = (agentId: number, agentName: string) => {
    setDuplicateAgentId(agentId)
    setDuplicateAgentName(agentName)
    setShowDuplicateModal(true)
  }

  const closeDuplicateModal = () => {
    setDuplicateAgentId(null)
    setDuplicateAgentName('')
    setShowDuplicateModal(false)
  }

  const handleDuplicateSuccess = async () => {
    closeDuplicateModal()
    await Promise.all([loadAgents(), loadQuota()])
  }

  const toggleAgentActivation = async (agentId: number, currentlyActive: boolean) => {
    try {
      // Récupérer le workspace_id actuel
      const currentWorkspaceId = localStorage.getItem('current_workspace_id')
      const params = currentWorkspaceId ? { workspace_id: currentWorkspaceId } : {}
      
      const endpoint = currentlyActive ? 'deactivate' : 'activate'
      await api.post(`/agents/${agentId}/${endpoint}`, {}, { params })
      
      // Recharger les agents et le quota
      await Promise.all([loadAgents(), loadQuota()])
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message
      if (err?.response?.status === 403) {
        // Quota dépassé
        alert(detail || t.agents.errors.quotaReached)
      } else {
        alert(detail || t.agents.errors.toggleActivation)
      }
    }
  }

  const uploadIcon = async (agentId: number, file: File) => {
    setIconUploadingId(agentId)
    try {
      const data = new FormData()
      data.append('file', file)
      await api.post(`/agents/${agentId}/icon`, data)
      await loadAgents()
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || t.agents.errors.uploadIcon
      alert(detail)
    } finally {
      setIconUploadingId(null)
    }
  }

  const onIconChange = (agentId: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      uploadIcon(agentId, file)
      event.target.value = ''
    }
  }

  const selectForChat = (agentId: number) => {
    try {
      localStorage.setItem('chat_selected_agent', String(agentId))
      window.dispatchEvent(new CustomEvent('chat-agent-selected', { detail: agentId }))
    } catch {}
  }

  const lastUpdated = useMemo(() => {
    if (!agents.length) return '—'
    const mostRecent = agents.reduce((acc, agent) => {
      if (!acc) return agent
      return new Date(agent.updated_at) > new Date(acc.updated_at) ? agent : acc
    })
    return dateFormatter.format(new Date(mostRecent.updated_at))
  }, [agents])

  return (
    <div className="relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_68%)]" />
      <div aria-hidden className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_15%_18%,rgba(236,72,153,0.14),transparent_60%),radial-gradient(circle_at_85%_18%,rgba(168,85,247,0.17),transparent_55%)] dark:bg-[radial-gradient(circle_at_12%_18%,rgba(236,72,153,0.12),transparent_55%),radial-gradient(circle_at_88%_20%,rgba(168,85,247,0.15),transparent_50%)]" />

      <div className="relative mx-auto flex max-w-[1600px] flex-col gap-4 px-1 py-3 sm:px-6 sm:py-6">
        {statsDrawerOpen && (
          <>
            <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm" onClick={closeStatsDrawer} aria-hidden />
            <aside className="fixed inset-y-0 right-0 z-[80] w-full max-w-xl border-l border-emerald-100/80 bg-white/95 shadow-2xl transition-transform dark:border-emerald-900/40 dark:bg-zinc-900/95">
              <div className="flex h-full flex-col">
                <header className="flex items-start justify-between gap-4 border-b border-emerald-100/80 px-6 py-4 dark:border-emerald-900/40">
                  <div className="space-y-1">
                    <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">{t.agents.statsDrawerTitle}</h2>
                    <p className="text-sm text-zinc-600 dark:text-zinc-300">{t.agents.statsDrawerSubtitle}</p>
                  </div>
                  <button
                    type="button"
                    onClick={closeStatsDrawer}
                    className="rounded-full p-2 text-zinc-500 transition hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
                  >
                    ✕
                  </button>
                </header>

                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
                  <section className="grid gap-4 sm:grid-cols-2">
                    {overviewStats.map(stat => (
                      <StatCard key={stat.label} label={stat.label} value={stat.value} description={stat.description} />
                    ))}
                  </section>

                  <StatsDetails
                    agents={agents}
                    ownerUsage={ownerUsage}
                    numberFormatter={numberFormatter}
                    dateFormatter={dateFormatter}
                  />
                </div>
              </div>
            </aside>
          </>
        )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={cancelDeleteAgent} />
          <div className="absolute inset-x-0 top-1/2 mx-auto w-full max-w-lg -translate-y-1/2 overflow-hidden rounded-3xl border border-red-200/70 bg-white/95 shadow-2xl shadow-red-300/30 dark:border-red-900/60 dark:bg-zinc-950/95">
            <div className="flex items-center justify-between border-b border-red-200/70 bg-red-50/60 px-6 py-4 dark:border-red-900/60 dark:bg-red-950/40">
              <div>
                <h2 className="text-lg font-semibold text-red-700 dark:text-red-200">{t.agents.deleteModal.title}</h2>
                <p className="text-sm text-red-600/80 dark:text-red-300/80">{t.agents.deleteModal.warning}</p>
              </div>
              <button
                onClick={cancelDeleteAgent}
                className="rounded-full border border-red-200/70 px-3 py-1 text-sm text-red-600 transition hover:bg-red-100/60 dark:border-red-800 dark:text-red-200 dark:hover:bg-red-900/60"
              >
                {t.common.close}
              </button>
            </div>
            <div className="space-y-4 px-6 py-6 text-sm text-zinc-700 dark:text-zinc-200">
              <p>
                {t.common.confirmDeletePrefix}{' '}
                <span className="font-semibold text-zinc-900 dark:text-white">{deleteTarget.name}</span>
                {' '}<span className="text-xs font-medium text-zinc-400 dark:text-zinc-500">(ID {deleteTarget.id})</span> ?
              </p>
              {deleteTarget.subject_ids?.length ? (
                <div className="rounded-2xl border border-red-200/70 bg-red-50/70 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
                  {interpolate(t.agents.deleteModal.linkedSubjectsWarning, { n: deleteTarget.subject_ids.length })}
                </div>
              ) : (
                <div className="rounded-2xl border border-red-200/70 bg-red-50/70 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
                  {t.agents.deleteModal.noSubjectsWarning}
                </div>
              )}
              {deleteError && (
                <div className="rounded-2xl border border-red-300 bg-white/90 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200">
                  {deleteError}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-red-200/60 bg-red-50/50 px-6 py-4 dark:border-red-900/50 dark:bg-red-950/30">
              <button
                onClick={cancelDeleteAgent}
                disabled={deletePending}
                className="rounded-full border border-zinc-200/70 px-4 py-2 text-sm font-semibold text-zinc-600 transition hover:bg-zinc-100/70 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800/60"
              >
                {t.common.cancel}
              </button>
              <button
                onClick={confirmDeleteAgent}
                disabled={deletePending}
                className="rounded-full border border-red-200/70 bg-red-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-600 disabled:opacity-50 dark:border-red-900/60"
              >
                {deletePending ? t.agents.agentCard.deleting : t.common.deletePermanently}
              </button>
            </div>
          </div>
        </div>
      )}
        {/* Create Agent Modal */}
        {createDrawerOpen && (
          <AgentEditorModal
            agent={null}
            subjects={subjects}
            subjectsLoading={subjectsLoading}
            llmModels={llmModels}
            llmModelsLoading={llmModelsLoading}
            isAdmin={isAdmin}
            systemPrompts={systemPrompts}
            teams={teams}
            teamsLoading={teamsLoading}
            onClose={(saved) => {
              setCreateDrawerOpen(false)
              if (saved) {
                loadAgents()
                loadQuota()
              }
            }}
            onSave={(savedAgent) => {
              try {
                localStorage.setItem('chat_selected_agent', String(savedAgent.id))
                window.dispatchEvent(new CustomEvent('chat-agent-selected', { detail: savedAgent.id }))
              } catch {}
            }}
          />
        )}


        <section className="flex gap-6">
          {/* Teams Sidebar - Desktop */}
          <div className="hidden lg:block">
            <TeamsSidebar
              teams={teams}
              loading={teamsLoading}
              selectedId={selectedTeamId}
              onSelect={setSelectedTeamId}
              totalAgents={agents.length}
              orphanCount={orphanAgentCount}
              agentCountByTeam={agentCountByTeam}
              onCreate={createTeam}
              onRename={renameTeam}
              onDelete={removeTeam}
              createModalOpen={teamModalOpen}
              onCreateModalOpenChange={setTeamModalOpen}
            />
          </div>

          {/* Teams Sidebar - Mobile Drawer */}
          {teamSidebarOpen && (
            <div className="lg:hidden fixed inset-0 z-40">
              <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                onClick={() => setTeamSidebarOpen(false)}
              />
              <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-white/95 shadow-2xl dark:bg-zinc-950/95">
                <div className="flex items-center justify-between border-b border-zinc-200/70 px-4 py-3 dark:border-zinc-800">
                  <h2 className="font-semibold text-zinc-900 dark:text-white">Équipes</h2>
                  <button
                    type="button"
                    onClick={() => setTeamSidebarOpen(false)}
                    className="rounded-full p-1.5 text-zinc-500 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="overflow-y-auto h-[calc(100%-56px)]">
                  <TeamsSidebar
                    teams={teams}
                    loading={teamsLoading}
                    selectedId={selectedTeamId}
                    onSelect={(id) => { setSelectedTeamId(id); setTeamSidebarOpen(false); }}
                    totalAgents={agents.length}
                    orphanCount={orphanAgentCount}
                    agentCountByTeam={agentCountByTeam}
                    onCreate={createTeam}
                    onRename={renameTeam}
                    onDelete={removeTeam}
                    createModalOpen={teamModalOpen}
                    onCreateModalOpenChange={setTeamModalOpen}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Main content */}
          <div className="flex-1 min-w-0 space-y-4">
            <div className="rounded-2xl border border-zinc-800/60 bg-zinc-900/80 p-4 sm:p-6">
              {/* ── Header row : titre + actions ── */}
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <h2 className="text-base sm:text-lg font-semibold text-white truncate">{t.agents.title}</h2>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {agents.length
                      ? `${agents.length} agent${agents.length > 1 ? 's' : ''} · ${t.agents.lastUpdated} ${lastUpdated}`
                      : t.agents.noAgents.split('.')[0]}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Équipes - mobile uniquement */}
                  <button
                    type="button"
                    className="lg:hidden flex items-center justify-center h-9 w-9 rounded-xl border border-zinc-700/60 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200 transition-colors"
                    onClick={() => setTeamSidebarOpen(true)}
                    title="Équipes"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>
                  {/* Stats */}
                  <button
                    type="button"
                    className="flex items-center justify-center h-9 w-9 rounded-xl border border-zinc-700/60 text-zinc-400 hover:bg-zinc-800 hover:text-emerald-400 transition-colors"
                    onClick={openStatsDrawer}
                    title={t.agents.statistics}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </button>
                  {/* Créer */}
                  <button
                    type="button"
                    className="flex items-center gap-1.5 rounded-xl bg-emerald-500 px-3 py-2 text-xs font-semibold text-white shadow-sm shadow-emerald-500/20 hover:bg-emerald-600 transition-colors"
                    onClick={() => navigate('/agents/new')}
                    data-onboarding="new-agent-btn"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="hidden sm:inline">{t.agents.createAgent}</span>
                    <span className="sm:hidden">Créer</span>
                  </button>
                </div>
              </div>

              {/* ── Quota compact ── */}
              {quota && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 px-3 py-1 text-xs">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
                    <span className="font-semibold text-emerald-400">{quota.current_active}</span>
                    <span className="text-zinc-500">
                      {quota.max_active !== null ? `/ ${quota.max_active}` : ''} {t.agents.quotaBadge.active}{quota.current_active > 1 && language === 'fr' ? 's' : ''}
                    </span>
                    {quota.available !== null && quota.available === 0 && (
                      <span className="ml-1 rounded-full bg-zinc-700 px-2 py-0.5 text-[10px] font-semibold text-zinc-300">
                        {t.agents.quotaBadge.quotaReached}
                      </span>
                    )}
                  </div>
                  {quota.global_total !== quota.total_agents && (
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-zinc-800/60 border border-zinc-700/40 px-3 py-1 text-xs text-zinc-500">
                      <span className="font-medium text-zinc-400">{quota.global_active}</span>
                      {quota.max_active !== null && <span>/ {quota.max_active}</span>}
                      <span>{language === 'fr' ? 'tous workspaces' : language === 'es' ? 'todos workspaces' : 'all workspaces'}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-zinc-800/60 bg-zinc-900/60 p-3 sm:p-4">
              {loading ? (
                <div className="py-12 text-center text-sm text-zinc-500">{t.agents.loading}</div>
              ) : error ? (
                <div className="py-6 text-sm text-red-600 dark:text-red-300">{error}</div>
              ) : agents.length === 0 ? (
                <div className="py-10 text-sm text-zinc-500">
                  {t.agents.noAgents}
                </div>
              ) : (
                <div className="grid gap-3">
                  {filteredAgents.map(agent => (
                    <AgentCard
                      key={agent.id}
                      agent={agent}
                      t={t}
                      language={language}
                      numberFormatter={numberFormatter}
                      dateFormatter={dateFormatter}
                      subjectMap={subjectMap}
                      iconUploadingId={iconUploadingId}
                      deletePending={deletePending}
                      deleteTarget={deleteTarget}
                      onIconChange={onIconChange}
                      onSelectForChat={selectForChat}
                      onNavigate={navigate}
                      onDuplicate={openDuplicateModal}
                      onToggleActivation={toggleAgentActivation}
                      onOpenMcp={openMcpDrawer}
                      onDelete={requestDeleteAgent}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

        </section>
      </div>

      {/* MCP Drawer */}
      <AgentMCPDrawer
        open={mcpDrawerOpen}
        onClose={closeMcpDrawer}
        agentId={mcpDrawerAgentId}
        agentName={mcpDrawerAgentName}
      />

      {/* Agent Onboarding Guide */}
      <AgentOnboardingGuide
        isEditModalOpen={isEditModalOpen}
        isCreateModalOpen={createDrawerOpen}
        isTeamModalOpen={teamModalOpen}
      />

      {isEditModalOpen && editingAgent && (
        <div className="fixed inset-0 z-[95]">
          <div className="absolute inset-0 bg-black/70 backdrop-blur" onClick={cancelEdit} aria-hidden />
          <div className="absolute inset-0 flex flex-col">
            <header className="flex items-center justify-between bg-white/90 px-6 py-4 shadow-md backdrop-blur dark:bg-zinc-950/80">
              <div className="flex items-center gap-4">
                {/* Avatar de l'agent */}
                <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-emerald-500 text-xl font-semibold text-white shadow-lg shadow-emerald-500/30">
                  {editingAgent.icon_preview_url ? (
                    <img src={editingAgent.icon_preview_url} alt={editingAgent.name} className="h-full w-full object-cover" />
                  ) : (
                    <span>{editingAgent.name.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-emerald-500 dark:text-emerald-300">{t.agents.sandbox.title}</p>
                  <h2 className="text-2xl font-semibold text-zinc-900 dark:text-white">{editingAgent.name}</h2>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">{t.agents.sandbox.subtitle}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="rounded-full border border-zinc-200/70 px-4 py-2 text-sm font-semibold text-zinc-600 transition hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
                  onClick={cancelEdit}
                >
                  {t.agents.sandbox.close}
                </button>
                <button
                  type="button"
                  className="rounded-full bg-emerald-500 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-600"
                  onClick={saveEdit}
                  disabled={savingEdit}
                >
                  {savingEdit ? t.agents.sandbox.saving : t.agents.sandbox.save}
                </button>
              </div>
            </header>

            <div className="flex-1 overflow-auto bg-gradient-to-br from-emerald-100/30 via-white to-zinc-50 dark:from-zinc-900 dark:via-zinc-950 dark:to-black">
              <div className="flex h-full flex-col gap-4 px-6 py-6">
                <div className="grid flex-1 gap-6 overflow-hidden lg:grid-cols-2">
                  <aside className="overflow-auto rounded-3xl border border-emerald-200/70 bg-white/90 p-5 shadow-xl dark:border-emerald-900/40 dark:bg-zinc-950/80">
                    <h3 className="text-sm font-semibold text-emerald-600 dark:text-emerald-300">{t.agents.sandbox.settings}</h3>
                    <EditPanel
                      agentId={editingAgent.id}
                      form={editForm}
                      onChange={setEditForm}
                      subjects={subjects}
                      subjectMap={subjectMap}
                      onToggleSubject={updateEditSubjectIds}
                      onCancel={cancelEdit}
                      onSave={saveEdit}
                      saving={savingEdit}
                      error={editError}
                      subjectsLoading={subjectsLoading}
                      llmModels={llmModels}
                      llmModelsLoading={llmModelsLoading}
                      isAdmin={isAdmin}
                      systemPrompts={systemPrompts}
                      teams={teams}
                      teamsLoading={teamsLoading}
                    />
                  </aside>

                  <section className="flex h-full flex-col overflow-auto rounded-3xl border border-emerald-200/60 bg-white/95 p-5 shadow-xl dark:border-emerald-900/40 dark:bg-zinc-950/85">
                    <header className="mb-4 flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">{t.agents.sandbox.playgroundTitle}</h3>
                        <p className="text-sm text-zinc-500 dark:text-zinc-400">{t.agents.sandbox.playgroundSubtitle}</p>
                      </div>
                      <span className="inline-flex items-center rounded-full border border-emerald-200/70 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-600 dark:border-emerald-900/40 dark:bg-emerald-900/40 dark:text-emerald-200">{t.agents.sandbox.quotaPreserved}</span>
                    </header>
                    <AgentSandboxChat
                      agent={editingAgent as AgentSummary}
                      conversationId={sandboxConversationId}
                      onConversationId={setSandboxConversationId}
                      onQuotaBypassNotice={() => {
                        /* hook for future analytics */
                      }}
                    />
                  </section>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Agent Modal */}
      {showDuplicateModal && duplicateAgentId && (
        <DuplicateAgentModal
          isOpen={showDuplicateModal}
          onClose={closeDuplicateModal}
          onSuccess={handleDuplicateSuccess}
          agentId={duplicateAgentId}
          agentName={duplicateAgentName}
        />
      )}
    </div>
  )
}

type StatCardProps = {
  label: string
  value: string
  description: string
}

function StatCard({ label, value, description }: StatCardProps) {
  return (
    <div className="rounded-3xl border border-emerald-100/70 bg-white/90 p-5 shadow-lg shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90">
      <p className="text-xs uppercase tracking-wide text-emerald-600 dark:text-emerald-300">{label}</p>
      <p className="mt-2 text-2xl font-semibold text-zinc-900 dark:text-white">{value}</p>
      <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">{description}</p>
    </div>
  )
}

type InfoChipProps = { label: string; value: string }

function InfoChip({ label, value }: InfoChipProps) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-zinc-200/70 px-3 py-1 text-xs text-zinc-600 dark:border-zinc-700 dark:text-zinc-300">
      <span className="text-[10px] uppercase tracking-wide text-zinc-400 dark:text-zinc-500">{label}</span>
      <span className="font-semibold text-zinc-700 dark:text-zinc-100">{value}</span>
    </span>
  )
}

type StatsDetailsProps = {
  agents: Agent[]
  ownerUsage: Agent['owner_usage'] | null
  numberFormatter: Intl.NumberFormat
  dateFormatter: Intl.DateTimeFormat
}

function StatsDetails({ agents, ownerUsage, numberFormatter, dateFormatter }: StatsDetailsProps) {
  const { t } = useAppLanguage()
  const interpolate = (template: string, vars: Record<string, string | number>) => {
    return Object.entries(vars).reduce(
      (acc, [key, val]) => acc.replace(`{${key}}`, String(val)),
      template
    )
  }
  
  const totalMessages = useMemo(
    () => agents.reduce((acc, agent) => acc + (agent.usage?.message_count ?? 0), 0),
    [agents]
  )

  const subjectCount = useMemo(() => {
    const unique = new Set<number>()
    agents.forEach(agent => agent.subject_ids.forEach(id => unique.add(id)))
    return unique.size
  }, [agents])

  const modelCount = useMemo(() => {
    const unique = new Set<string>()
    agents.forEach(agent => {
      if (agent.model_id) unique.add(agent.model_id)
    })
    return unique.size
  }, [agents])

  const lastCreated = useMemo(() => {
    if (!agents.length) return null
    const sorted = [...agents].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    return sorted[0]
  }, [agents])

  const mostActive = useMemo(() => {
    if (!agents.length) return []
    return [...agents]
      .sort((a, b) => (b.usage?.message_count ?? 0) - (a.usage?.message_count ?? 0))
      .slice(0, 5)
  }, [agents])

  const limit = ownerUsage?.limit ?? null
  const ownerCount = ownerUsage?.message_count ?? 0
  const utilisation = limit ? Math.min(Math.round((ownerCount / limit) * 100), 999) : null

  return (
    <div className="space-y-6">
      <section className="grid gap-4 md:grid-cols-2">
        <div className="rounded-3xl border border-emerald-100/70 bg-white/95 p-5 shadow-lg shadow-emerald-500/10 dark:border-emerald-900/40 dark:bg-zinc-950/90">
          <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">{t.agents.stats.usageGlobal}</h3>
          <p className="mt-2 text-3xl font-bold text-emerald-600 dark:text-emerald-300">
            {numberFormatter.format(ownerCount)}
          </p>
          <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">{t.agents.stats.messagesOrg}</p>
          <div className="mt-4 space-y-2 text-xs text-zinc-500 dark:text-zinc-400">
            <div className="flex items-center justify-between">
              <span>{t.agents.stats.totalAgents}</span>
              <span className="font-semibold text-zinc-700 dark:text-zinc-200">{numberFormatter.format(agents.length)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>{t.agents.stats.knowledgeBases}</span>
              <span className="font-semibold text-zinc-700 dark:text-zinc-200">{numberFormatter.format(subjectCount)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>{t.agents.stats.aiUsed}</span>
              <span className="font-semibold text-zinc-700 dark:text-zinc-200">{numberFormatter.format(modelCount)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>{t.agents.stats.totalMessagesByAgents}</span>
              <span className="font-semibold text-zinc-700 dark:text-zinc-200">{numberFormatter.format(totalMessages)}</span>
            </div>
          </div>
        </div>

        <div className="rounded-3xl border border-emerald-100/70 bg-white/95 p-5 shadow-lg shadow-emerald-500/10 dark:border-emerald-900/40 dark:bg-zinc-950/90">
          <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">{t.agents.stats.globalQuotaTitle}</h3>
          <div className="mt-3">
            <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-300">
              {limit != null ? numberFormatter.format(limit) : '∞'}
            </p>
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">{t.agents.stats.messagesAllowed}</p>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-zinc-500 dark:text-zinc-400">
              <span>{t.agents.stats.utilization}</span>
              <span className="font-semibold text-zinc-700 dark:text-zinc-200">
                {utilisation != null ? `${utilisation}%` : t.agents.stats.unlimited}
              </span>
            </div>
            {utilisation != null && (
              <div className="mt-2 h-2 rounded-full bg-emerald-100 dark:bg-emerald-950">
                <div
                  className="h-full rounded-full bg-emerald-500"
                  style={{ width: `${Math.max(utilisation, 4)}%` }}
                />
              </div>
            )}
            {limit != null && (
              <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
                {interpolate(t.agents.stats.remainingMessages, { n: numberFormatter.format(Math.max(limit - ownerCount, 0)) })}
              </p>
            )}
          </div>
          {lastCreated && (
            <p className="mt-4 text-xs text-zinc-500 dark:text-zinc-400">
              {t.agents.stats.lastAgentCreated}:{' '}
              <span className="font-semibold text-zinc-700 dark:text-zinc-200">{lastCreated.name}</span>{' '}
              ({dateFormatter.format(new Date(lastCreated.created_at))})
            </p>
          )}
        </div>
      </section>

      <section className="rounded-3xl border border-emerald-100/70 bg-white/95 p-5 shadow-lg shadow-emerald-500/10 dark:border-emerald-900/40 dark:bg-zinc-950/90">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">{t.agents.stats.mostActiveAgents}</h3>
          <span className="text-xs text-zinc-500 dark:text-zinc-400">{t.agents.stats.top5Period}</span>
        </div>
        {mostActive.length === 0 ? (
          <p className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">{t.agents.stats.noMessagesYet}</p>
        ) : (
          <ul className="mt-4 space-y-3 text-sm text-zinc-600 dark:text-zinc-300">
            {mostActive.map(agent => {
              const limitText =
                agent.usage?.limit != null
                  ? `${numberFormatter.format(agent.usage.limit)} ${t.agents.stats.maxLabel}`
                  : t.agents.stats.noCap
              return (
                <li key={agent.id} className="flex items-start justify-between gap-3 rounded-2xl border border-zinc-200/70 bg-white/80 px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900/80">
                  <div className="space-y-1">
                    <p className="font-semibold text-zinc-900 dark:text-white">{agent.name}</p>
                    {agent.subtitle && <p className="text-xs text-zinc-500 dark:text-zinc-400">{agent.subtitle}</p>}
                    <p className="text-xs uppercase tracking-wide text-emerald-600 dark:text-emerald-300">{agent.slug}</p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">{agent.subject_ids.length} {t.agents.stats.docsSuffix} — {limitText}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold text-emerald-600 dark:text-emerald-300">
                      {numberFormatter.format(agent.usage?.message_count ?? 0)}
                    </p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">{t.agents.stats.messagesSuffix}</p>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </div>
  )
}

type AgentAvatarProps = {
  agent: Agent
  uploading: boolean
  onIconChange: (event: ChangeEvent<HTMLInputElement>) => void
}

function AgentAvatar({ agent, uploading, onIconChange }: AgentAvatarProps) {
  return (
    <label className="relative inline-flex h-12 w-12 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-emerald-500 text-lg font-semibold text-white shadow-lg shadow-emerald-500/30">
      {agent.icon_preview_url ? (
        <img src={agent.icon_preview_url} alt={agent.name} className="h-full w-full object-cover" />
      ) : (
        <span>{agent.name.charAt(0).toUpperCase()}</span>
      )}
      <input type="file" className="hidden" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={onIconChange} />
      {uploading && (
        <span className="absolute inset-0 flex items-center justify-center bg-white/70 text-xs text-emerald-600">Upload…</span>
      )}
    </label>
  )
}

type EditPanelProps = {
  agentId: number
  form: FormState
  onChange: (state: FormState) => void
  subjects: Subject[]
  subjectMap: Map<number, Subject>
  onToggleSubject: (id: number) => void
  onCancel: () => void
  onSave: () => void
  saving: boolean
  error: string | null
  subjectsLoading: boolean
  llmModels: LlmModelOption[]
  llmModelsLoading: boolean
  isAdmin?: boolean
  systemPrompts?: SystemPromptListItem[]
  teams?: { id: number; title: string }[]
  teamsLoading?: boolean
}

function EditPanel({ agentId, form, onChange, subjects, onToggleSubject, onCancel, onSave, saving, error, subjectsLoading, llmModels, llmModelsLoading, isAdmin, systemPrompts, teams, teamsLoading }: EditPanelProps) {
  const { t } = useAppLanguage()
  const [modelPickerOpen, setModelPickerOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'general' | 'knowledge' | 'whatsapp' | 'memory'>('general')
  const [availableScopes, setAvailableScopes] = useState<MemoryScope[]>([])
  const [loadingScopes, setLoadingScopes] = useState(false)
  const [selectedScopeIds, setSelectedScopeIds] = useState<number[]>([])
  const [selectedAgentType, setSelectedAgentType] = useState<AgentType | null>(null)
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false)
  
  // Paramètres avancés - persisté en localStorage (admin uniquement)
  const [showAdvanced, setShowAdvanced] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('agent_show_advanced') === 'true'
    }
    return false
  })
  
  const toggleAdvanced = () => {
    const newValue = !showAdvanced
    setShowAdvanced(newValue)
    localStorage.setItem('agent_show_advanced', String(newValue))
  }

  // Charger les scopes disponibles et les scopes assignés à l'agent
  useEffect(() => {
    const loadScopes = async () => {
      setLoadingScopes(true)
      try {
        const scopesRes = await memoryApi.listScopes()
        setAvailableScopes(scopesRes.items.filter(s => s.scope_type === 'tenant'))
        
        if (agentId) {
          const assignedScopeIds = await memoryApi.getAgentScopes(agentId)
          setSelectedScopeIds(assignedScopeIds)
        }
      } catch (err) {
        console.error('Failed to load scopes:', err)
      } finally {
        setLoadingScopes(false)
      }
    }
    loadScopes()
  }, [agentId])

  const handleScopeToggle = (scopeId: number) => {
    setSelectedScopeIds(prev =>
      prev.includes(scopeId)
        ? prev.filter(id => id !== scopeId)
        : [...prev, scopeId]
    )
  }

  const handleSaveScopes = async () => {
    if (!agentId) return
    try {
      await memoryApi.updateAgentScopes(agentId, selectedScopeIds)
    } catch (err) {
      console.error('Failed to save scopes:', err)
    }
  }

  const handleAgentTypeChange = (type: AgentType | null) => {
    setSelectedAgentType(type)
    setShowTemplateLibrary(!!type)
  }

  const handleTemplateSelect = (template: PromptTemplate) => {
    onChange({ ...form, instructions: template.content })
    setShowTemplateLibrary(false)
  }
  
  return (
    <div className="mt-3 rounded-2xl border border-emerald-100/70 bg-white/90 p-3 text-sm shadow-inner dark:border-emerald-900/50 dark:bg-zinc-900/80">
      <h4 className="text-sm font-semibold text-zinc-900 dark:text-white mb-3">Modifier l'agent</h4>
      
      {/* Onglets */}
      <div className="flex gap-2 mb-3 border-b border-gray-200 dark:border-zinc-700">
        <button
          type="button"
          onClick={() => setActiveTab('general')}
          className={`px-4 py-2 text-sm font-medium transition border-b-2 ${
            activeTab === 'general'
              ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
        >
          Général
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('knowledge')}
          className={`px-4 py-2 text-sm font-medium transition border-b-2 ${
            activeTab === 'knowledge'
              ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
          data-onboarding="knowledge-tab"
        >
          Base de connaissances
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('whatsapp')}
          className={`px-4 py-2 text-sm font-medium transition border-b-2 ${
            activeTab === 'whatsapp'
              ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
        >
          WhatsApp
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('memory')}
          className={`px-4 py-2 text-sm font-medium transition border-b-2 ${
            activeTab === 'memory'
              ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
        >
          🧠 Mémoire
        </button>
      </div>

      {/* Contenu des onglets */}
      {activeTab === 'general' ? (
      <div className="grid gap-3">
        <div className="space-y-1">
          <label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">Nom</label>
          <input
            className="w-full rounded-xl border border-emerald-100/70 bg-transparent px-3 py-2 text-sm text-zinc-700 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/60 dark:text-zinc-200"
            value={form.name}
            onChange={e => onChange({ ...form, name: e.target.value })}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">Sous-titre</label>
          <input
            className="w-full rounded-xl border border-emerald-100/70 bg-transparent px-3 py-2 text-sm text-zinc-700 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/60 dark:text-zinc-200"
            value={form.subtitle}
            onChange={e => onChange({ ...form, subtitle: e.target.value })}
            placeholder="Tagline visible dans le chat"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">👥 Équipe</label>
          <select
            className="w-full rounded-xl border border-emerald-100/70 bg-white/80 px-3 py-2 text-sm text-zinc-700 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/60 dark:bg-zinc-900/70 dark:text-zinc-200"
            value={form.teamId ?? ''}
            onChange={e => onChange({ ...form, teamId: e.target.value ? Number(e.target.value) : null })}
            disabled={teamsLoading}
          >
            <option value="">Sans équipe</option>
            {teams?.map(team => (
              <option key={team.id} value={team.id}>{team.title}</option>
            ))}
          </select>
        </div>
        <div className="space-y-2">
          <AgentTypeSelector
            value={selectedAgentType}
            onChange={handleAgentTypeChange}
          />
        </div>
        {showTemplateLibrary && selectedAgentType && (
          <div className="p-3 bg-zinc-900/50 border border-zinc-700 rounded-xl">
            <TemplateLibrary
              agentType={selectedAgentType}
              currentPrompt={form.instructions}
              onSelectTemplate={handleTemplateSelect}
            />
          </div>
        )}
        <div className="space-y-1">
          <label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">💬 Personnalité</label>
          <textarea
            className="h-16 w-full rounded-xl border border-emerald-100/70 bg-transparent px-3 py-2 text-sm text-zinc-700 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/60 dark:text-zinc-200"
            value={form.instructions}
            onChange={e => onChange({ ...form, instructions: e.target.value })}
          />
          {form.instructions && (
            <button
              type="button"
              onClick={() => setShowTemplateLibrary(!showTemplateLibrary)}
              className="text-xs text-emerald-600 dark:text-emerald-400 hover:text-emerald-500 dark:hover:text-emerald-300 transition-colors mt-1"
            >
              {showTemplateLibrary ? '✕ Masquer les templates' : '📚 Changer de template'}
            </button>
          )}
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">Limite</label>
            <input
              type="number"
              min={1}
              className="w-full rounded-xl border border-emerald-100/70 bg-transparent px-3 py-2 text-sm text-zinc-700 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/60 dark:text-zinc-200"
              value={form.messageLimit}
              onChange={e => onChange({ ...form, messageLimit: e.target.value })}
              placeholder="Aucun plafond"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 flex items-center">
              🧠 Intelligence
              <HelpTooltip content="Plus le niveau est élevé, plus les réponses sont précises mais peuvent être plus lentes." position="right" />
            </label>
            <div className="flex gap-2">
              <input
                readOnly
                className="min-w-0 flex-1 cursor-default rounded-xl border border-emerald-100/70 bg-white/80 px-3 py-2 text-sm text-zinc-700 dark:border-emerald-900/60 dark:bg-zinc-900/70 dark:text-zinc-200"
                value={
                  form.modelId
                    ? llmModels.find(m => m.name === form.modelId)?.name || form.modelId
                    : t.agents.agentEditor.modelDefault
                }
              />
              <button
                type="button"
                onClick={() => setModelPickerOpen(true)}
                className="shrink-0 rounded-full border border-emerald-200/80 bg-emerald-50 px-4 py-2 text-xs font-semibold text-emerald-800 transition hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200 dark:hover:bg-emerald-900/40"
              >
                {t.agents.modelPicker.browse}
              </button>
            </div>
            <AgentModelPickerModal
              open={modelPickerOpen}
              onClose={() => setModelPickerOpen(false)}
              models={llmModels}
              value={form.modelId}
              onApply={name => onChange({ ...form, modelId: name })}
              loading={llmModelsLoading}
              defaultOptionLabel={t.agents.agentEditor.modelDefault}
            />
          </div>
        </div>
        {error && <p className="text-xs text-red-600 dark:text-red-300">{error}</p>}
      </div>
      ) : activeTab === 'knowledge' ? (
      <div className="grid gap-3">
        <div className="space-y-1">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.disableOwnKnowledge}
              onChange={e => onChange({ ...form, disableOwnKnowledge: e.target.checked })}
              className="h-4 w-4 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 flex items-center">
              Réponses strictes
              <HelpTooltip content="Si activé, l'assistant ne répondra QU'avec les informations de vos documents. Il ne pourra pas utiliser ses connaissances générales." position="right" />
            </span>
          </label>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            Si activé, l'assistant ne répondra qu'avec les informations de votre base de connaissances.
          </p>
        </div>
        <div className="space-y-1" data-onboarding="knowledge-base-section">
          <label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 flex items-center">
            Base de connaissances
            <HelpTooltip content="Documents et fichiers que votre assistant peut consulter pour répondre aux questions." position="right" />
          </label>
          <div className="max-h-28 overflow-y-auto rounded-2xl border border-zinc-200/70 bg-white/80 p-2 dark:border-zinc-800 dark:bg-zinc-900/80">
            {subjectsLoading && <div className="text-xs text-zinc-500">Chargement…</div>}
            {!subjectsLoading && subjects.length === 0 && <div className="text-xs text-zinc-500">Aucune base de connaissances disponible.</div>}
            {!subjectsLoading && subjects.map(subject => (
              <label key={subject.id} className="flex items-center gap-2 py-1">
                <input type="checkbox" checked={form.subjectIds.includes(subject.id)} onChange={() => onToggleSubject(subject.id)} />
                <span className="text-xs text-zinc-600 dark:text-zinc-300">{subject.title}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            Sélectionnez les documents que l'assistant peut consulter pour répondre.
          </p>
        </div>

        {/* Toggle Paramètres avancés (admin uniquement) */}
        {isAdmin && (
          <div className="mt-2 pt-2 border-t border-zinc-200/50 dark:border-zinc-700/50">
            <button
              type="button"
              onClick={toggleAdvanced}
              className="flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200 transition"
            >
              <span className={`transform transition-transform ${showAdvanced ? 'rotate-90' : ''}`}>▶</span>
              <span className="uppercase tracking-wide">Paramètres avancés</span>
              {showAdvanced && <span className="text-violet-500 dark:text-violet-400">(admin)</span>}
            </button>
            
            {showAdvanced && (
              <div className="mt-3 space-y-3 pl-4 border-l-2 border-violet-200/50 dark:border-violet-800/50">
                {/* Top K */}
                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-wide text-violet-600 dark:text-violet-400">
                    🔒 Top K
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    className="w-full rounded-xl border border-violet-200/70 bg-transparent px-3 py-2 text-sm text-zinc-700 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200 dark:border-violet-900/60 dark:text-zinc-200"
                    value={form.topK}
                    onChange={e => onChange({ ...form, topK: e.target.value })}
                    placeholder="10"
                  />
                  <p className="text-xs text-violet-600/70 dark:text-violet-400/70">
                    Nombre de contextes RAG. Auto-configuré selon le modèle pour les utilisateurs.
                  </p>
                </div>

                {/* Rerank Top N */}
                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-wide text-violet-600 dark:text-violet-400">
                    🎯 Rerank Top N
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    className="w-full rounded-xl border border-violet-200/70 bg-transparent px-3 py-2 text-sm text-zinc-700 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200 dark:border-violet-900/60 dark:text-zinc-200"
                    value={form.rerankTopN}
                    onChange={e => onChange({ ...form, rerankTopN: e.target.value })}
                    placeholder="5"
                  />
                  <p className="text-xs text-violet-600/70 dark:text-violet-400/70">
                    Résultats finaux après reranking sémantique (1-20, défaut: 5).
                  </p>
                </div>

                {/* System Prompt Override */}
                {systemPrompts && systemPrompts.length > 0 && (
                  <div className="space-y-1">
                    <label className="text-xs uppercase tracking-wide text-violet-600 dark:text-violet-400">
                      🔒 Prompt Système
                    </label>
                    <select
                      className="w-full rounded-xl border border-violet-200/70 bg-white/80 px-3 py-2 text-sm text-zinc-700 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200 dark:border-violet-900/60 dark:bg-zinc-900/70 dark:text-zinc-200"
                      value={form.systemPromptId ?? ''}
                      onChange={e => onChange({ ...form, systemPromptId: e.target.value ? Number(e.target.value) : null })}
                    >
                      <option value="">— Prompt par défaut —</option>
                      {systemPrompts.map(prompt => (
                        <option key={prompt.id} value={prompt.id}>
                          {prompt.name} {prompt.is_default ? '(défaut)' : ''} — v{prompt.version}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-violet-600/70 dark:text-violet-400/70">
                      Remplace le prompt système par défaut pour cet agent.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {error && <p className="text-xs text-red-600 dark:text-red-300">{error}</p>}
      </div>
      ) : activeTab === 'whatsapp' ? (
        <WhatsAppTab agentId={agentId} />
      ) : activeTab === 'memory' ? (
        <div className="space-y-4">
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl border border-emerald-100/70 hover:border-emerald-300 transition-colors dark:border-emerald-900/60 dark:hover:border-emerald-700">
              <input
                type="checkbox"
                checked={form.memoryEnabled}
                onChange={e => onChange({ ...form, memoryEnabled: e.target.checked })}
                className="mt-0.5 w-4 h-4 rounded border-zinc-300 text-emerald-500"
              />
              <div>
                <span className="text-sm font-medium text-zinc-700 dark:text-zinc-200">
                  💼 Mémoire Business (Tenant)
                </span>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                  Active la mémoire partagée pour vos connaissances métier
                </p>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl border border-emerald-100/70 hover:border-emerald-300 transition-colors dark:border-emerald-900/60 dark:hover:border-emerald-700">
              <input
                type="checkbox"
                checked={form.memoryEndUserEnabled}
                onChange={e => onChange({ ...form, memoryEndUserEnabled: e.target.checked })}
                className="mt-0.5 w-4 h-4 rounded border-zinc-300 text-emerald-500"
              />
              <div>
                <span className="text-sm font-medium text-zinc-700 dark:text-zinc-200">
                  👤 Mémoire End-User (Clients)
                </span>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                  Mémorise des informations spécifiques par client identifié
                </p>
              </div>
            </label>
          </div>

          {form.memoryEnabled && (
            <div className="mt-4 p-4 rounded-xl border border-emerald-100/70 bg-white/50 dark:border-emerald-900/60 dark:bg-zinc-800/50">
              <div className="flex items-center justify-between mb-3">
                <h5 className="text-sm font-medium text-zinc-700 dark:text-zinc-200">
                  📚 Scopes Assignés
                </h5>
                {loadingScopes && (
                  <span className="text-xs text-zinc-500">Chargement...</span>
                )}
              </div>
              
              {availableScopes.length === 0 && !loadingScopes ? (
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  Aucun scope disponible. <a href="/memory" className="underline text-emerald-600 hover:text-emerald-700">Créez-en un</a>.
                </p>
              ) : (
                <div className="space-y-2">
                  {availableScopes.map(scope => (
                    <label
                      key={scope.id}
                      className="flex items-start gap-2 p-2 rounded-lg hover:bg-emerald-50 dark:hover:bg-zinc-700/30 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedScopeIds.includes(scope.id)}
                        onChange={() => handleScopeToggle(scope.id)}
                        className="mt-0.5 w-4 h-4 rounded border-zinc-300 text-emerald-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-zinc-700 dark:text-zinc-200">{scope.name}</div>
                        {scope.description && (
                          <div className="text-xs text-zinc-500 dark:text-zinc-400">{scope.description}</div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
              
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-3">
                {selectedScopeIds.length === 0 
                  ? "💡 Aucun scope sélectionné - tous les scopes business seront utilisés"
                  : `✓ ${selectedScopeIds.length} scope(s) sélectionné(s)`
                }
              </p>
            </div>
          )}

          {/* Section Extraction Automatique */}
          <div className="mt-4 p-4 rounded-xl border border-purple-100/70 bg-gradient-to-r from-purple-50/50 to-indigo-50/50 dark:border-purple-900/60 dark:from-purple-900/20 dark:to-indigo-900/20">
            <h5 className="text-sm font-medium text-zinc-700 dark:text-zinc-200 mb-3">
              🤖 Extraction Automatique
            </h5>
            
            <label className="flex items-start gap-3 cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={form.autoExtractFacts}
                onChange={e => onChange({ ...form, autoExtractFacts: e.target.checked })}
                className="mt-0.5 w-4 h-4 rounded border-zinc-300 text-purple-500"
              />
              <div>
                <span className="text-sm text-zinc-700 dark:text-zinc-200">
                  Activer l'apprentissage automatique
                </span>
                <p className="text-xs text-zinc-500 mt-0.5">
                  L'agent apprendra automatiquement des conversations (nom, préférences, etc.)
                </p>
              </div>
            </label>
            
            {form.autoExtractFacts && (
              <div className="space-y-3 mt-3 pl-7">
                <div>
                  <label className="text-xs text-zinc-600 dark:text-zinc-400 mb-1 block">
                    Seuil de confiance minimum: {Math.round((form.autoExtractMinConfidence || 0.75) * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0.5"
                    max="1"
                    step="0.05"
                    value={form.autoExtractMinConfidence || 0.75}
                    onChange={e => onChange({ ...form, autoExtractMinConfidence: parseFloat(e.target.value) })}
                    className="w-full h-2 bg-zinc-200 rounded-lg appearance-none cursor-pointer dark:bg-zinc-700"
                  />
                  <p className="text-xs text-zinc-400 mt-1">
                    Plus le seuil est élevé, moins de faits seront extraits mais plus ils seront fiables
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="p-3 rounded-xl bg-blue-50 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800/30">
            <p className="text-xs text-blue-600 dark:text-blue-400">
              💡 Gérez vos scopes et faits mémoire dans la page <a href="/memory" className="underline hover:text-blue-700">Mémoire</a>
            </p>
          </div>
        </div>
      ) : null}

      {/* Boutons (communs aux onglets General, Knowledge et Memory) */}
      {(activeTab === 'general' || activeTab === 'knowledge' || activeTab === 'memory') && (
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            type="button"
            className="rounded-full border border-emerald-200/70 bg-emerald-500 px-4 py-1.5 text-xs font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600 disabled:opacity-50"
            onClick={onSave}
            disabled={saving}
            data-onboarding="agent-save-btn"
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          <button
            type="button"
            className="rounded-full border border-zinc-200/70 px-4 py-1.5 text-xs font-semibold text-zinc-600 transition hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
            onClick={onCancel}
          >
            Annuler
          </button>
        </div>
      )}
    </div>
  )
}
